import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LinkSimple, Trash, X } from "@phosphor-icons/react";
import api, { formatApiError } from "./../api";
import { BrutalButton, BrutalInput } from "./Brutal";

/** v410.2 — Store SKU -> Ontario Master product mapping manager.
 *  Surfaces SKUs that arrived on real orders but couldn't reserve, lets the
 *  owner map each to a product (retro-reserving open orders), and manages
 *  existing mappings. */
export function SkuMappingDialog({ onClose, onChanged }) {
  const [products, setProducts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [catalogUnmapped, setCatalogUnmapped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [manualSku, setManualSku] = useState("");
  const [picks, setPicks] = useState({}); // sku -> product_id

  const load = async () => {
    setLoading(true);
    try {
      const [p, m, u] = await Promise.all([
        api.get("/partner-admin/products"),
        api.get("/partner-admin/sku-map"),
        api.get("/partner-admin/unmapped-skus"),
      ]);
      setProducts(p.data.products || []);
      setMappings(m.data.mappings || []);
      setUnmapped(u.data.unmapped || []);
      setCatalogUnmapped(u.data.catalog_unmapped || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveMapping = async (sku) => {
    const product_id = picks[sku];
    if (!sku || !product_id) {
      toast.error("Pick a product first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/partner-admin/sku-map", { sku, product_id });
      const bits = [];
      if (data.retro_reserved_units > 0) bits.push(`${data.retro_reserved_units} unit(s) on open orders reserved`);
      if (data.retro_committed_units > 0) bits.push(`stock deducted for ${data.retro_committed_units} unit(s) on PAID orders`);
      toast.success(bits.length ? `Mapped ${sku} — ${bits.join(", ")}` : `Mapped ${sku}`);
      setManualSku("");
      setPicks((prev) => ({ ...prev, [sku]: "" }));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeMapping = async (sku) => {
    if (!window.confirm(`Remove the mapping for ${sku}?`)) return;
    try {
      await api.delete(`/partner-admin/sku-map/${encodeURIComponent(sku)}`);
      toast.success("Mapping removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const ProductSelect = ({ sku }) => (
    <select
      className="border-2 border-zinc-300 rounded-lg text-sm px-2 py-1.5 bg-white min-w-[220px] flex-1"
      value={picks[sku] || ""}
      onChange={(e) => setPicks((prev) => ({ ...prev, [sku]: e.target.value }))}
      data-testid={`sku-pick-${sku}`}
    >
      <option value="">Pick a product…</option>
      {products.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name} — {p.available} available{p.sku ? ` (${p.sku})` : ""}
        </option>
      ))}
    </select>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border-2 border-zinc-900 w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="sku-map-dialog">
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <LinkSimple size={18} weight="bold" className="text-[#8a6a1c]" />
            Store SKU mapping
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="sku-map-close">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-5">
          {loading ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : (
            <>
              {products.length === 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Ontario Master holds no products yet — transfer stock to that account first, then map SKUs here.
                </p>
              )}

              {unmapped.length > 0 && (
                <div className="space-y-2" data-testid="unmapped-skus-block">
                  <p className="text-sm font-semibold text-red-800">
                    Needs mapping — these SKUs arrived on real orders but reserved nothing ({unmapped.length})
                  </p>
                  {unmapped.map((u) => (
                    <div key={u.sku} className="border border-red-200 bg-red-50/50 rounded-lg p-3 space-y-2" data-testid={`unmapped-sku-${u.sku}`}>
                      <p className="text-sm">
                        <span className="font-mono font-semibold">{u.sku}</span>
                        {u.name ? <span className="text-zinc-600"> · {u.name}</span> : null}
                        {u.qty > 0 && (
                          <span className="text-[11px] text-zinc-400"> · {u.qty} unit(s) on {u.orders.length} open order(s)</span>
                        )}
                      </p>
                      {u.paid_qty > 0 && (
                        <p className="text-[11px] font-semibold text-red-700" data-testid={`paid-exposure-${u.sku}`}>
                          {u.paid_qty} unit(s) already PAID on {u.paid_orders.length} order(s) — stock was never deducted. Mapping fixes it instantly.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <ProductSelect sku={u.sku} />
                        <BrutalButton size="sm" variant="dark" disabled={busy}
                          onClick={() => saveMapping(u.sku)} data-testid={`map-sku-${u.sku}`}>
                          Map
                        </BrutalButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {catalogUnmapped.length > 0 && (
                <div className="space-y-2" data-testid="catalog-unmapped-block">
                  <p className="text-sm font-semibold text-amber-800">
                    Store catalog — not mapped yet ({catalogUnmapped.length})
                  </p>
                  <p className="text-xs text-zinc-500">
                    The Store's official SKU list. Map each to a product now so day-one orders reserve stock instantly.
                  </p>
                  {catalogUnmapped.map((u) => (
                    <div key={u.sku} className="border border-amber-200 bg-amber-50/40 rounded-lg p-3 space-y-2" data-testid={`catalog-sku-${u.sku}`}>
                      <p className="text-sm">
                        <span className="font-mono font-semibold">{u.sku}</span>
                        <span className="text-zinc-600"> · {u.name}</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <ProductSelect sku={u.sku} />
                        <BrutalButton size="sm" variant="dark" disabled={busy}
                          onClick={() => saveMapping(u.sku)} data-testid={`map-sku-${u.sku}`}>
                          Map
                        </BrutalButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-semibold text-zinc-900">Add a mapping</p>
                <div className="flex flex-wrap gap-2">
                  <BrutalInput placeholder="Store SKU (exact, case-sensitive)" value={manualSku}
                    onChange={(e) => setManualSku(e.target.value)} className="w-52" data-testid="manual-sku-input" />
                  <ProductSelect sku={manualSku || "__manual"} />
                  <BrutalButton size="sm" variant="dark" disabled={busy || !manualSku.trim()}
                    onClick={() => saveMapping(manualSku.trim())} data-testid="manual-map-btn">
                    Map
                  </BrutalButton>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm font-semibold text-zinc-900">Current mappings ({mappings.length})</p>
                {mappings.length === 0 ? (
                  <p className="text-xs text-zinc-400">None yet. Orders also match automatically when a product's own SKU field equals the Store SKU exactly.</p>
                ) : (
                  mappings.map((m) => (
                    <div key={m.sku} className="flex items-center gap-2 text-sm border border-zinc-200 rounded-lg px-3 py-2" data-testid={`mapping-${m.sku}`}>
                      <span className="font-mono font-semibold">{m.sku}</span>
                      <span className="text-zinc-400">→</span>
                      <span className="text-zinc-800">{m.product_name || m.product_id}</span>
                      <button onClick={() => removeMapping(m.sku)}
                        className="ml-auto text-zinc-400 hover:text-red-600" data-testid={`delete-mapping-${m.sku}`}>
                        <Trash size={15} weight="bold" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
